import {
    Badge,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    useMediaQuery,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { useMutation, useQuery } from "@apollo/client";
import { PROFILE } from "@/graphql/queries/profile";
import { useEffect, useState } from "react";
import AccountBoxRoundedIcon from "@mui/icons-material/AccountBoxRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppRoundedIcon from "@mui/icons-material/ExitToAppRounded";
import { useRouter } from "next/router";
import { REVOKE_TOKEN } from "@/graphql/mutations/auth";
import { localStorageWrapper } from "@/utils/storage";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import useGoftino from "../support/useGoftino";
import { numberWithCommas } from "@/utils/numbers";
import { clearApolloCache } from "@/graphql/client";
import { useTheme } from "@emotion/react";
import { unsetSupportId } from "@/utils/support";
import useChatWidgetRouter from "../support/useChatWidgetRouter";
import useOnlineHawzahChatWidget from "../support/useOnlineHawzahChatWidget";

export default function AccountMenu() {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const router = useRouter();
    const theme = useTheme();
    const mobileViewMatches = useMediaQuery(theme.breakpoints.down("sm"));

    const { data: { profile } = {}, loading: profileLoading } = useQuery(
        PROFILE,
        {
            fetchPolicy: "cache-first",
        }
    );

    const isProfileFullFilled = profile?.meta?.isFullFilled ?? false;
    const profileCompletionPercentage = profile?.meta?.completedPercentage ?? 0;

    const activeChatWidget = useChatWidgetRouter();
    // Always call hooks unconditionally - they check internally if they should run
    useGoftino(activeChatWidget);
    useOnlineHawzahChatWidget(activeChatWidget);

    const [revokeToken, {}] = useMutation(REVOKE_TOKEN);

    const handleLogout = () => {
        const logout = () => {
            localStorageWrapper.clearAuthValues().clearImpersonate();

            handleClose();
            router.push("/login?next=/courses");

            clearApolloCache()?.then(() => {
                setTimeout(() => {
                    unsetSupportId();
                }, 300);
            });
        };

        revokeToken({
            variables: {
                refreshToken: localStorageWrapper.getRefreshToken(),
            },
            onCompleted: () => logout(),
            onError: () => logout(),
        });
    };

    const pathName = router?.pathname;
    useEffect(() => {
        const destPath = "/profile/update";
        if (
            !profileLoading &&
            !profile?.gender &&
            !pathName.startsWith(destPath)
        ) {
            router.push(destPath);
        }
    }, [pathName, profileLoading]);

    useEffect(() => {
        try {
            if (profile && "hj" in window) {
                console.debug("-- set hotjar identify --");
                window.hj("identify", profile?.userId, {
                    saId: profile?.userId,
                });
            }
            if (profile && "clarity" in window) {
                console.debug("-- set clarity identify --");
                window.clarity("identify", `${profile?.userId}`);
            }
        } catch {}
    }, [profile]);

    return (
        <Box sx={{ position: "relative" }}>
            <IconButton
                id="account-menu-button"
                aria-controls={open ? "account-menu-button" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                disableRipple={!mobileViewMatches}
                size="medium"
            >
                <>
                    {!mobileViewMatches && (
                        <Box sx={{ marginRight: "6px" }}>
                            <ProfileInfo profile={profile} />
                        </Box>
                    )}
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <AccountCircle
                            sx={(theme) => ({
                                "&.MuiSvgIcon-root": {
                                    fontSize: "26px",
                                    color: theme.palette.primary.main,
                                    opacity: isProfileFullFilled ? 1 : 0.7,
                                },
                            })}
                        />
                        {!isProfileFullFilled && (
                            <CircularProgress
                                variant="determinate"
                                value={profileCompletionPercentage}
                                size="26px"
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    ".MuiCircularProgress-circle": {
                                        stroke: "#1553C9",
                                    },
                                }}
                            />
                        )}
                    </Box>
                </>
            </IconButton>
            <Menu
                id="account-menu-button"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                sx={{
                    "& .MuiPaper-root": {
                        minWidth: "120px",
                    },
                }}
            >
                {mobileViewMatches && (
                    <>
                        <Box sx={{ padding: "10px 15px" }}>
                            <ProfileInfo profile={profile} />
                        </Box>
                        <Divider />
                    </>
                )}

                <MenuItem
                    onClick={() => {
                        router.push("/profile");
                        handleClose();
                    }}
                    sx={{ padding: "10px 15px" }}
                >
                    <AccountBoxRoundedIcon sx={{ color: "gray.dark" }} />
                    <div className="mr-[10px]">پروفایل</div>
                    {!isProfileFullFilled && (
                        <Badge
                            variant="dot"
                            max={99}
                            color="error"
                            overlap="rectangular"
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            sx={{ marginLeft: "8px" }}
                        ></Badge>
                    )}
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        router.push("/settings");
                        handleClose();
                    }}
                    sx={{ padding: "10px 15px" }}
                >
                    <SettingsIcon sx={{ color: "gray.dark" }} />
                    <div className="mr-[10px]">تنظیمات</div>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        router.push("/financial/donate");
                        handleClose();
                    }}
                    sx={{ padding: "10px 15px" }}
                >
                    <CheckCircleIcon sx={{ color: "gray.dark" }} />
                    <div className="mr-[10px]">کمک مالی</div>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        router.push("/financial/credit");
                        handleClose();
                    }}
                    sx={{ padding: "10px 15px" }}
                >
                    <CreditCardIcon sx={{ color: "gray.dark" }} />
                    <div className="mr-[10px]">افزایش اعتبار</div>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        router.push("/financial/transactions");
                        handleClose();
                    }}
                    sx={{ padding: "10px 15px" }}
                >
                    <ReceiptIcon sx={{ color: "gray.dark" }} />
                    <div className="mr-[10px]">تراکنش‌ها</div>
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        handleLogout();
                    }}
                    sx={{ padding: "10px 15px" }}
                >
                    <ExitToAppRoundedIcon sx={{ color: "gray.dark" }} />
                    <div className="mr-[10px]">خروج</div>
                </MenuItem>
            </Menu>
        </Box>
    );
}

function ProfileInfo({ profile }) {
    return (
        <Typography
            component="p"
            sx={{
                color: "black",
                fontSize: "0.9rem",
                marginTop: "3px",
                fontWeight: "500",
                display: "inline",
                whiteSpace: "nowrap",
            }}
        >
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                fontSize="0.8rem"
            >
                <Box
                    sx={{
                        fontSize: "inherit",
                        width: "100%",
                        fontWeight: "bold",
                    }}
                >
                    {(profile?.firstName ?? "") +
                        " " +
                        (profile?.lastName ?? "")}
                </Box>
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="start"
                    alignItems="center"
                    fontSize="inherit"
                    textAlign="right"
                    width="100%"
                >
                    <Box sx={{ fontSize: "0.7rem" }}>اعتبار:</Box>
                    <Box sx={{ marginLeft: "4px", fontSize: "inherit" }}>
                        {numberWithCommas(profile?.balance ?? 0)}
                    </Box>
                    <Box sx={{ marginLeft: "4px", fontSize: "inherit" }}>
                        تومان
                    </Box>
                </Box>
            </Box>
        </Typography>
    );
}
